import { Building, Floor, Space } from '@miq/domain/pv';
import { TenantSpace, TenantSummaryMap, TenantSummary, TenantSpaceFormatted } from './tenant-summary.model';
import { DASH } from '@miq/utils';
import { formatFlatFloorSpaceDeatils, hasValidLease, UNKNOWN_TENANT } from '../../shared/utils';

export const getTenantLabels = (tenantSpace: TenantSpace): string => {
  const mainTenant = tenantSpace?.party_organization_display_name || UNKNOWN_TENANT;
  const tenants = tenantSpace?.tenants?.map(t => t || UNKNOWN_TENANT) || [];
  return [mainTenant, ...tenants].sort((a, b) => a.localeCompare(b)).join('\n');
};

const FLOOR_SPECITICATION_MAPPING = {
  true: 'Entire',
  false: 'Partial',
};
export const getFloorLabel = (tenantSpace: TenantSpace): string => {
  const floorNumber = tenantSpace?.floor_number || DASH;
  const floorSpecification = FLOOR_SPECITICATION_MAPPING[`${tenantSpace?.full_floor_f}`] ?? '';
  return `${floorSpecification} ${floorNumber}`.trim();
};

export const hasSelectedTenant = (space: Record<string, any>, tenantIdList: number[], currentUsage = '') => {
  if (currentUsage != '' && space?.lease?.ref_property_usage_type_desc != currentUsage) {
    return false;
  }
  if (hasValidLease(space)) {
    const { lease } = space;
    return (
      lease?.tenant_info?.length > 0 && (lease.tenant_info as any[]).some(t => tenantIdList.includes(t.company_id))
    );
  }
  return false;
};

/**
 * Validate the Lease and if it is valid, return the space area that should be used to calculate the foot print of a Tenant
 * @param lease
 * @returns arrangement_space_area || total_leased_space || 0
 */
export const getSpaceForFootPrint = (lease: Record<string, any>) => {
  if (hasValidLease({ lease })) {
    return lease.arrangement_space_area ?? lease.total_leased_space ?? 0;
  }
  return 0;
};

/**
 * From the Tenant, Floor and Space data, reduce the data to only the necessary fields
 * @param tenant
 * @param floor
 * @param space
 * @returns
 */
export const reduceTenantInfo = (
  tenant: Record<string, any>,
  floor: Pick<Floor, 'floor_index' | 'floor_number'>,
  space: Space
): TenantSpace => {
  const { lease } = space;
  return {
    // Floor Data
    floor_number: floor.floor_number ?? null,
    floor_index: floor.floor_index ?? null,
    // Suite Data
    suite_number: space.suite_number ?? null,
    suite_id: space.suite_id ?? null,
    // Lease Data
    space_id: lease.space_id ?? null,
    ref_property_usage_type_desc: lease.ref_property_usage_type_desc ?? null,
    arrangement_space_area: lease.arrangement_space_area || null,
    total_leased_space: lease.total_leased_space ?? null,
    sublease_f: lease.sublease_f ?? null,
    space_tenancy_status_desc: lease.space_tenancy_status_desc ?? null,
    space_tenancy_end_date: lease.space_tenancy_end_date ?? null,
    lease_end_date: lease.lease_end_date ?? null,
    full_floor_f: lease.full_floor_f ?? null,
    // Tenant
    party_organization_display_name: tenant.party_organization_display_name ?? null,
    tenants:
      lease.tenant_info
        ?.map(t => (t.company_id !== tenant.company_id ? t.party_organization_display_name : null))
        .filter(Boolean) ?? [],
    lease_record_type_desc: space?.lease?.lease_record_type_desc ?? null,
  };
};

/**
 * From the Building data {@link Building} creates a Map for each different Tenant
 * only with the necessary data and the related Floor & Suite
 * @param building
 * @returns Map with only the necessary data from the Tenant {@link TenantSummaryMap}
 */
export const convertBuildingToTenantSummaryMap = (building: Building): TenantSummaryMap => {
  const tenantsSummaryMap = new Map<number, TenantSummary>();
  if (building?.floors == null || building.floors.length === 0) {
    return tenantsSummaryMap;
  }

  building.floors.forEach(floor => {
    if (floor.spaces?.length > 0) {
      const leasedSpaces = floor.spaces.filter(hasValidLease);
      if (leasedSpaces.length > 0) {
        leasedSpaces.forEach(leasedS => {
          const { lease } = leasedS;
          if (lease.tenant_info == null || lease.tenant_info?.length === 0) {
            return;
          }

          // Iterate from all the tenants in the lease
          lease.tenant_info.forEach(tenant => {
            const tenantId = tenant.company_id;
            const tenantSum = tenantsSummaryMap.has(tenantId)
              ? tenantsSummaryMap.get(tenantId)
              : ({
                  id: tenantId,
                  tenantName: tenant.party_organization_display_name,
                  footPrint: 0,
                  spaces: [],
                } as TenantSummary);

            // This is used when the selected Tenant is only one to avoid unnecessary iterations
            tenantSum.footPrint += getSpaceForFootPrint(lease);
            const tSpaces: TenantSpace = reduceTenantInfo(
              tenant,
              {
                floor_number: floor.floor_number,
                floor_index: floor.floor_index,
              },
              leasedS
            );
            tenantSum.spaces.push(tSpaces);
            tenantsSummaryMap.set(tenantId, tenantSum);
          });
        });
      }
    }
  });
  return tenantsSummaryMap;
};

/**
 * Base on a Tenant Space object, format the data to be displayed in the table
 * @param tenantSpace {@link TenantSpace}
 * @returns
 */
export const formatTenantSummary = (tenantSpace: TenantSpace, currentUsage = ''): TenantSpaceFormatted => {
  const isSelectedPropertyType = currentUsage === '' || currentUsage === tenantSpace.ref_property_usage_type_desc;
  const sfSize = formatFlatFloorSpaceDeatils.getSF({ lease: tenantSpace } as unknown as Space);
  return {
    tenant: getTenantLabels(tenantSpace),
    propertyType: tenantSpace?.ref_property_usage_type_desc || DASH,
    floor: isSelectedPropertyType ? getFloorLabel(tenantSpace) : DASH,
    suite: isSelectedPropertyType ? tenantSpace?.suite_number || DASH : DASH,
    sf: sfSize.formattedValue,
    sfRaw: sfSize.rawValue,
    leaseType: isSelectedPropertyType
      ? formatFlatFloorSpaceDeatils.getLeaseTypeLabel({
          sublease_f: tenantSpace?.sublease_f,
          space_tenancy_status_desc: tenantSpace?.space_tenancy_status_desc,
          lease_record_type_desc: tenantSpace?.lease_record_type_desc,
        })
      : DASH,
    endDate: isSelectedPropertyType ? formatFlatFloorSpaceDeatils.getLeaseEndDate(tenantSpace).formattedValue : DASH,
    rowClass: isSelectedPropertyType ? '' : 'fsd-table-row--highlighted',
    isSelectedPropertyType,
  };
};

/**
 * Iterate through the building spaces and calculate the total foot print of the selected tenants
 * @param tenantIdList
 * @param building
 * @returns
 */
export const calculateFootPrint = (tenantIdList: number[], floors: Floor[], currentUsage = '') => {
  if (tenantIdList == null || tenantIdList.length === 0 || floors == null || floors.length === 0) {
    return 0;
  }
  const spaces = floors
    .map(floor => floor.spaces)
    .flat()
    .filter(space => hasSelectedTenant(space, tenantIdList, currentUsage));

  return spaces.reduce((acc, space) => acc + getSpaceForFootPrint(space.lease), 0);
};
